import { Component, EventEmitter, Input, Output } from "@angular/core";

/**
 * Shared Clipping Studio color swatch row: preset dots, optional transparent,
 * and optional custom color via icn-color-picker.
 */
@Component({
  selector: "evrideo-clipping-studio-color-swatch-picker",
  templateUrl: "./clipping-studio-color-swatch-picker.component.html",
  styleUrls: ["./clipping-studio-color-swatch-picker.component.scss"],
})
export class ClippingStudioColorSwatchPickerComponent {
  @Input() colors: string[] = [];
  /** Current value (preset string, custom hex/rgba, or "transparent"). */
  @Input() value: string | null = null;
  @Input() allowCustom = true;
  @Input() allowTransparent = false;

  @Output() valueChange = new EventEmitter<string>();

  get isTransparentSelected(): boolean {
    const v = (this.value || "").trim().toLowerCase();
    return v === "" || v === "transparent";
  }

  get isCustomSelected(): boolean {
    if (this.isTransparentSelected) {
      return false;
    }
    const active = (this.value || "").trim().toLowerCase();
    return !this.colors.some((c) => c.toLowerCase() === active);
  }

  private static readonly LIGHT_RING_FALLBACK = "#7f8590";
  /** Relative luminance above this uses the gray ring (white / near-white on light UI). */
  private static readonly LIGHT_LUMINANCE_THRESHOLD = 0.82;

  /** Selection ring color — mirrors the active swatch; gray for transparent / light tones. */
  get selectedRingColor(): string {
    if (this.isTransparentSelected) {
      return ClippingStudioColorSwatchPickerComponent.LIGHT_RING_FALLBACK;
    }
    return this.ringColorFor((this.value || "").trim() || "#212121");
  }

  /** Ring color for a swatch: same as fill, or gray when too light on white background. */
  ringColorFor(color: string): string {
    return this.isLightColor(color)
      ? ClippingStudioColorSwatchPickerComponent.LIGHT_RING_FALLBACK
      : color;
  }

  /** Value shown in icn-color-picker when opening the palette. */
  get pickerDisplayValue(): string {
    if (this.isTransparentSelected) {
      return "#ffffff";
    }
    const raw = (this.value || "").trim();
    return raw || "#ffffff";
  }

  selectPreset(color: string): void {
    this.valueChange.emit(color);
  }

  selectTransparent(): void {
    this.valueChange.emit("transparent");
  }

  isPresetSelected(color: string): boolean {
    if (this.isTransparentSelected) {
      return false;
    }
    return (this.value || "").trim().toLowerCase() === color.toLowerCase();
  }

  onCustomColorChange(color: string): void {
    this.valueChange.emit(this.normalizeHexColor(color));
  }

  /**
   * ngx-color-picker with alpha may emit #RRGGBBAA; strip alpha and expand #RGB
   * so stored values stay 6-digit hex until the render text parser is extended.
   */
  private normalizeHexColor(raw: string): string {
    const value = (raw || "").trim();
    const match = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
    if (!match) {
      return value;
    }

    let hex = match[1];
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((ch) => ch + ch)
        .join("");
    } else if (hex.length === 8) {
      hex = hex.slice(0, 6);
    }

    return `#${hex.toLowerCase()}`;
  }

  private isLightColor(raw: string): boolean {
    const rgb = this.parseRgb(raw);
    if (!rgb) {
      return false;
    }
    const [r, g, b] = rgb.map((c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return (
      luminance >=
      ClippingStudioColorSwatchPickerComponent.LIGHT_LUMINANCE_THRESHOLD
    );
  }

  private parseRgb(raw: string): [number, number, number] | null {
    const value = (raw || "").trim();
    const hexMatch = value.match(
      /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i
    );
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((ch) => ch + ch)
          .join("");
      } else if (hex.length === 8) {
        hex = hex.slice(0, 6);
      }
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }

    const rgbMatch = value.match(
      /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
    );
    if (rgbMatch) {
      return [
        Math.min(255, Number(rgbMatch[1])),
        Math.min(255, Number(rgbMatch[2])),
        Math.min(255, Number(rgbMatch[3])),
      ];
    }

    return null;
  }
}
